import { useState, useEffect, useRef } from "react";
import { Menu, X, LayoutDashboard, BarChart3, CreditCard, Settings, LogOut, Loader2, MessagesSquare } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import BrandLogo from "../BrandLogo";
import ThemeToggle from "../ThemeToggle";
import Button from "../Button";
// import AfterLoginHomeHeader from "../ProfileDropdown";
import { useAuth } from "@/app/providers/AuthProvider";
import { useLiveChatStore } from "@/features/live-chat/store/liveChatStore";
import Modal from "../Modal";
import Setting from "@/features/setting/pages/Setting";
import AfterLoginHomeHeader from "../ProfileDropdown";

interface NavLinkProps {
    href: string;
    children: React.ReactNode;
    isActive: boolean;
    isExternal?: boolean;
}

const NavLink = ({ href, children, isActive, isExternal = false }: NavLinkProps) => {
    return (
        <div className="relative group h-full flex items-center">
            <Link
                to={href}
                target={isExternal ? "_blank" : undefined}
                className={`text-base font-medium transition-colors ${isActive
                    ? "text-foreground"
                    : "text-muted-foreground dark:text-gray-300 hover:text-foreground"
                    }`}
            >
                {children}
            </Link>
            <div
                className={`absolute bottom-0 left-0 h-0.5 bg-blue-600 transition-all duration-300 ease-out translate-y-4
          ${isActive ? "w-full" : "w-0 group-hover:w-full"}`}
            />
        </div>
    );
};

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const { user, logout, isLoading } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const mobileMenuRef = useRef<HTMLDivElement>(null);
    const menuButtonRef = useRef<HTMLButtonElement>(null);
    const [isSettingOpen, setIsSettingOpen] = useState(false);

    const isBuilder = pathname.startsWith('/chatbot-builder');

    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            await logout();
            setIsMenuOpen(false);
            setIsSettingOpen(false);
            navigate('/login');
        } finally {
            setIsLoggingOut(false);
        }
    };

    // Global live chat unread badge
    const { totalUnreadCount } = useLiveChatStore();

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isMenuOpen &&
                mobileMenuRef.current &&
                menuButtonRef.current &&
                !mobileMenuRef.current.contains(event.target as Node) &&
                !menuButtonRef.current.contains(event.target as Node)
            ) {
                setIsMenuOpen(false);
            }
        };

        if (isMenuOpen) {
            // Use a small timeout to avoid closing immediately after opening
            const timeoutId = setTimeout(() => {
                document.addEventListener("mousedown", handleClickOutside);
            }, 0);

            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }
    }, [isMenuOpen]);

    const VITE_NEXTJS_URL = import.meta.env.VITE_NEXTJS_URL;




    if (isLoading) return null;

    // Completely hide the global navbar when inside the chatbot builder
    // The ChatbotBuilderLayout provides its own full-screen layout and header
    if (isBuilder) {
        return null;
    }

    return (
        <nav
            className="sticky top-0 z-50 w-full bg-white dark:bg-gray-900 navbar-shadow transition-all duration-300"
        >
            <div className={`${pathname === '/live-chat' ? "w-full" : "container"} px-6 flex h-14 items-center justify-between py-4`}>
                <div className="flex items-center gap-6">
                    {/* Standard Pages: Show Logo */}
                    <Link to={`${VITE_NEXTJS_URL}`} target="_blank" className="flex items-center gap-2">
                        <BrandLogo />
                    </Link>
                </div>

                <div className="flex items-center gap-6">
                    <div className="hidden lg:flex items-center text-[#323232] dark:text-white gap-6">
                        <div className="flex items-center gap-6">
                            {user ? (
                                <>
                                    <NavLink href="/dashboard" isActive={pathname === "/dashboard"}>
                                        <div className="flex items-center gap-2">
                                            <LayoutDashboard className="h-4 w-4" /> Dashboard
                                        </div>
                                    </NavLink>
                                    <NavLink href="#" isActive={pathname.includes("/analytics")}>
                                        <div className="flex items-center gap-2">
                                            <BarChart3 className="h-4 w-4" /> Analytics (coming soon)
                                        </div>
                                    </NavLink>
                                    <NavLink href="/plans" isActive={pathname === "/plans"}>
                                        <div className="flex items-center gap-2">
                                            <CreditCard className="h-4 w-4" /> Plans
                                        </div>
                                    </NavLink>
                                </>
                            ) : (
                                <>
                                    {/* <NavLink
                                        href="/chat-demo"
                                        isActive={pathname === "/chat-demo"}
                                    >
                                        Demo
                                    </NavLink> */}
                                    <NavLink
                                        href="/login"
                                        isActive={pathname?.startsWith("/login")}
                                    >
                                        Log in
                                    </NavLink>
                                </>
                            )}
                        </div>

                        {user && (
                            <NavLink href="/live-chat" isActive={pathname === '/live-chat'}>
                                <div className="flex items-center gap-2">
                                    <div className="relative">
                                        <MessagesSquare className="h-4 w-4" />
                                        {totalUnreadCount > 0 && (
                                            <span className="absolute -top-2 -right-1.5 inline-flex items-center justify-center min-w-3.5 h-3.5 px-1 rounded-full bg-red-500 text-white text-[8px] font-bold">
                                                {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                                            </span>
                                        )}
                                    </div>
                                    Chats
                                </div>
                            </NavLink>
                        )}
                        <span className="w-0.5 h-8 bg-gray-200 dark:bg-gray-700"></span>
                        <ThemeToggle />
                        <div className="hidden lg:flex">
                            {user ? (
                                <AfterLoginHomeHeader />
                            ) : (
                                <Link to="/register">
                                    <Button className="text-center rounded-full text-base py-1 px-6 h-auto">
                                        Try for Free
                                    </Button>
                                </Link>
                            )}
                        </div>
                    </div>

                    <div className="lg:hidden flex items-center gap-2">
                        {user && (
                            <Link
                                to="/live-chat"
                                className={`relative p-1.5 text-gray-600 dark:text-gray-300 transition-colors ${pathname === '/live-chat' ? 'bg-gray-100 dark:bg-gray-800 text-blue-600 dark:text-blue-400' : ''
                                    }`}
                                title="Live Chat"
                            >
                                <MessagesSquare className="h-5 w-5" />
                                {totalUnreadCount > 0 && (
                                    <span className="absolute -top-1.5 -right-2.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold">
                                        {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                                    </span>
                                )}
                            </Link>
                        )}
                        <ThemeToggle />
                        <button className="flex text-[#323232] dark:text-white" onClick={() => setIsMenuOpen(!isMenuOpen)} ref={menuButtonRef}>
                            {isMenuOpen ? (
                                <X className="h-6 w-6" />
                            ) : (
                                <Menu className="h-6 w-6" />
                            )}
                        </button>
                    </div>
                </div>

            </div>

            {/* Mobile menu */}
            {isMenuOpen && (
                <div className="lg:hidden border-b" ref={mobileMenuRef}>
                    <div className="container py-4 flex flex-col gap-3">
                        {user ? (
                            <>
                                <Link
                                    to="/dashboard"
                                    className={`px-2 py-2 text-base font-medium flex items-center gap-2 ${pathname === "/dashboard" ? "text-blue-600 dark:text-white" : "text-muted-foreground dark:text-gray-300"}`}
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    <LayoutDashboard className="h-4 w-4" /> Dashboard
                                </Link>
                                <Link
                                    to="#"
                                    className={`px-2 py-2 text-base font-medium flex items-center gap-2 ${pathname.includes("/analytics") ? "text-blue-600 dark:text-white" : "text-muted-foreground dark:text-gray-300"}`} onClick={() => setIsMenuOpen(false)}
                                >
                                    <BarChart3 className="h-4 w-4" /> Analytics (coming soon)
                                </Link>
                                <Link
                                    to="/plans"
                                    className={`px-2 py-2 text-base font-medium flex items-center gap-2 ${pathname === "/plans" ? "text-blue-600 dark:text-white" : "text-muted-foreground dark:text-gray-300"}`}
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    <CreditCard className="h-4 w-4" /> Plans
                                </Link>
                                <button
                                    className={`px-2 py-2 text-base font-medium flex items-center gap-2 ${isSettingOpen ? "text-blue-600 dark:text-white" : "text-muted-foreground dark:text-gray-300"}`}
                                    onClick={() => {
                                        setIsMenuOpen(false);
                                        setIsSettingOpen(true);
                                    }}
                                >
                                    <Settings className="h-4 w-4" /> Settings
                                </button>
                                <button
                                    className="w-full flex font-medium items-center gap-2 px-2 py-2 text-left text-red-600 hover:text-red-600 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={handleLogout}
                                    disabled={isLoggingOut}
                                >
                                    {isLoggingOut ? <Loader2 size={16} className="animate-spin" /> : <LogOut size={16} />}
                                    {isLoggingOut ? 'Logging out...' : 'Logout'}
                                </button>
                            </>
                        ) : (
                            <>
                                <Link
                                    to="/"
                                    className="px-2 py-2 text-base font-medium text-muted-foreground dark:text-gray-300 hover:text-foreground transition-colors"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    Home
                                </Link>
                                {/* <Link
                                    to="/chat-demo"
                                    className="px-2 py-2 text-base font-medium text-muted-foreground dark:text-gray-300 hover:text-foreground transition-colors"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    Demo
                                </Link> */}
                                <Link
                                    to="/login"
                                    className="px-2 py-2 text-base font-medium text-muted-foreground dark:text-gray-300 hover:text-foreground transition-colors"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    Log in
                                </Link>
                                <div className="flex flex-col gap-2">
                                    <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                                        <Button className="w-full">
                                            Try for Free
                                        </Button>
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            <Modal
                isOpen={isSettingOpen}
                onClose={() => setIsSettingOpen(false)}
                title={
                    <div>
                        <h1 className="text-xl font-bold">Settings</h1>
                        <p className="text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5">
                            Manage your account settings and preferences
                        </p>
                    </div>
                }
                size="4xl"
                showFooter={true}
                actions={[
                    {
                        label: (
                            <div className="flex items-center gap-2">
                                {isLoggingOut ? <Loader2 size={16} className="animate-spin" /> : <LogOut size={16} />}
                                {isLoggingOut ? 'Logging out...' : 'Logout'}
                            </div>
                        ),
                        color: "danger",
                        onClick: handleLogout,
                        disabled: isLoggingOut
                    }
                ]}
            >
                <div className="p-1">
                    <Setting hideHeader={true} />
                </div>
            </Modal>
        </nav>
    );
}


export default Navbar;
